import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import { getErrorMessage } from '../utils/api';
import { buildApiUrl } from '../config/api';

interface CoachDashboardProps {
  onBackToLanding?: () => void;
  onExit?: () => void;
  onNewEvaluation?: () => void;
  onLoadEvaluation?: (evaluationId: number) => void;
  onCreateNewForPlayer?: (playerName: string, playerAge: number, playerPosition: string) => void;
  onViewPlayerDashboard?: (playerId: number) => void;
  onViewVideoKinematics?: () => void;
  onViewVideoKinematicsForPlayer?: (playerId: number) => void;
  initialTab?: 'evaluations' | 'players';
}

interface Evaluation {
  id: number;
  player_id?: number | null;
  player_name: string;
  player_age: number;
  player_position: string;
  weighted_index: number;
  created_at: string;
  has_ai_feedback: boolean;
}

interface Player {
  player_id: number | null;
  first_name: string;
  last_name: string;
  age: number;
  position: string;
  email?: string;
  whatsapp_phone?: string;
  evaluation_count: number;
  latest_evaluation_id?: number;
  latest_evaluation_date?: string;
  latest_weighted_index?: number;
}

interface CoachInfo {
  first_name: string;
  last_name: string;
}

// Using buildApiUrl helper instead of API_BASE to ensure correct /api prefix

const CoachDashboard: React.FC<CoachDashboardProps> = ({ onBackToLanding, onExit, onNewEvaluation, onLoadEvaluation, onCreateNewForPlayer, onViewPlayerDashboard, onViewVideoKinematics, onViewVideoKinematicsForPlayer, initialTab = 'evaluations' }) => {
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [coachInfo, setCoachInfo] = useState<CoachInfo | null>(null);
  const [activeTab, setActiveTab] = useState<'evaluations' | 'players'>(initialTab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Load coach information
        try {
          const coachInfoResponse = await fetch(buildApiUrl('/coaches/me'), {
            headers: {
              'Authorization': `Bearer ${token}`
            },
            credentials: 'include',
            mode: 'cors'
          });
          
          if (coachInfoResponse.ok) {
            const coachInfoData = await coachInfoResponse.json();
            if (coachInfoData.coach) {
              setCoachInfo({
                first_name: coachInfoData.coach.first_name,
                last_name: coachInfoData.coach.last_name
              });
            }
          } else {
            console.error('Failed to load coach information:', coachInfoResponse.status);
          }
        } catch (err: any) {
          console.error('Network error loading coach information:', err);
          if (err.message?.includes('fetch') || err.message?.includes('Load failed')) {
            throw new Error('Cannot connect to server. Please check your internet connection and try again.');
          }
        }
        
        // Load evaluations
        try {
          const evaluationsResponse = await fetch(buildApiUrl('/evaluations/list'), {
            headers: {
              'Authorization': `Bearer ${token}`
            },
            credentials: 'include',
            mode: 'cors'
          });
          
          if (evaluationsResponse.ok) {
            const evaluationsData = await evaluationsResponse.json();
            setEvaluations(evaluationsData.evaluations || []);
          } else {
            const errorData = await evaluationsResponse.json().catch(() => ({}));
            setError(getErrorMessage(errorData, 'Failed to load evaluations'));
          }
        } catch (err: any) {
          console.error('Network error loading evaluations:', err);
          if (err.message?.includes('fetch') || err.message?.includes('Load failed')) {
            throw new Error('Cannot connect to server. Please check your internet connection and try again.');
          }
          const errorData = await err.json?.().catch(() => ({}));
          setError(getErrorMessage(errorData, 'Failed to load evaluations'));
        }

        // Load players
        try {
          const playersResponse = await fetch(buildApiUrl('/coaches/my-players'), {
            headers: {
              'Authorization': `Bearer ${token}`
            },
            credentials: 'include',
            mode: 'cors'
          });
          
          if (playersResponse.ok) {
            const playersData = await playersResponse.json();
            console.log('🔍 DEBUG: My Players API Response:', playersData);
            console.log('🔍 DEBUG: Coach teams:', playersData.debug?.coach_teams);
            console.log('🔍 DEBUG: Coach teams raw:', playersData.debug?.coach_teams_raw);
            console.log('🔍 DEBUG: Players found:', playersData.debug?.players_found);
            console.log('🔍 DEBUG: Sample player teams:', playersData.debug?.sample_player_teams);
            console.log('🔍 DEBUG: Players array:', playersData.players);
            setPlayers(playersData.players || []);
          } else {
            const errorData = await playersResponse.json().catch(() => ({}));
            const errorMessage = getErrorMessage(errorData, 'Failed to load players');
            console.error('Failed to load players:', errorData);
            setError(errorMessage);
            // Set empty players array so UI doesn't show stale data
            setPlayers([]);
          }
        } catch (err: any) {
          console.error('Network error loading players:', err);
          if (err.message?.includes('fetch') || err.message?.includes('Load failed')) {
            throw new Error('Cannot connect to server. Please check your internet connection and try again.');
          }
          const errorData = await err.json?.().catch(() => ({}));
          const errorMessage = getErrorMessage(errorData, 'Failed to load players');
          setError(errorMessage);
          setPlayers([]);
        }
      } catch (err: any) {
        console.error('Error loading data:', err);
        const errorMessage = err.message || 'Failed to load data. Please try again.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleLoadEvaluation = async (evaluationId: number) => {
    if (onLoadEvaluation) {
      onLoadEvaluation(evaluationId);
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Coach Dashboard</h1>
          {coachInfo && (
            <p style={{ 
              margin: '0.5rem 0 0 0', 
              color: '#797b84', 
              fontSize: '1rem',
              fontWeight: 400 
            }}>
              {coachInfo.first_name} {coachInfo.last_name}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {onViewVideoKinematics && (
            <button 
              onClick={onViewVideoKinematics} 
              className="button primary"
              style={{ 
                background: '#4ade80', 
                borderColor: '#4ade80'
              }}
            >
              📹 Video Kinematics
            </button>
          )}
          {onNewEvaluation && (
            <button onClick={onNewEvaluation} className="button primary">
              New Evaluation
            </button>
          )}
          {onBackToLanding && (
            <button onClick={onBackToLanding} className="back-btn">Back</button>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', borderBottom: '2px solid #383b48' }}>
        <button
          onClick={() => setActiveTab('evaluations')}
          style={{
            padding: '0.75rem 1.5rem',
            background: activeTab === 'evaluations' ? '#567ad8' : 'transparent',
            color: activeTab === 'evaluations' ? '#fdfeff' : '#797b84',
            border: 'none',
            borderBottom: activeTab === 'evaluations' ? '3px solid #567ad8' : '3px solid transparent',
            cursor: 'pointer',
            fontWeight: activeTab === 'evaluations' ? 600 : 400,
            transition: 'all 0.3s ease'
          }}
        >
          Evaluations ({evaluations.length})
        </button>
        <button
          onClick={() => setActiveTab('players')}
          style={{
            padding: '0.75rem 1.5rem',
            background: activeTab === 'players' ? '#567ad8' : 'transparent',
            color: activeTab === 'players' ? '#fdfeff' : '#797b84',
            border: 'none',
            borderBottom: activeTab === 'players' ? '3px solid #567ad8' : '3px solid transparent',
            cursor: 'pointer',
            fontWeight: activeTab === 'players' ? 600 : 400,
            transition: 'all 0.3s ease'
          }}
        >
          My Players ({players.length})
        </button>
      </div>

      <div className="dashboard-content">
        {activeTab === 'evaluations' ? (
          evaluations.length === 0 ? (
            <div className="no-evaluations">
              <p>No evaluations yet. Create your first evaluation to get started.</p>
              {onNewEvaluation && (
                <button onClick={onNewEvaluation} className="button primary" style={{ marginTop: '1rem' }}>
                  Create First Evaluation
                </button>
              )}
            </div>
          ) : (
            <div className="evaluations-list">
              <h2>Previous Evaluations</h2>
              {evaluations.map((evaluation) => (
                <div key={evaluation.id} className="evaluation-card">
                  <div className="evaluation-card-header">
                    <h3>{evaluation.player_name}</h3>
                    <span className="evaluation-date">
                      {new Date(evaluation.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="evaluation-card-details">
                    <div className="detail-item">
                      <span className="detail-label">Age:</span>
                      <span className="detail-value">{evaluation.player_age}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Position:</span>
                      <span className="detail-value">{evaluation.player_position}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Overall Score:</span>
                      <span className="detail-value">{evaluation.weighted_index.toFixed(2)}/10</span>
                    </div>
                    {evaluation.has_ai_feedback && (
                      <div className="detail-item">
                        <span className="detail-label">AI Feedback:</span>
                        <span className="detail-value" style={{ color: '#4ade80' }}>✓ Available</span>
                      </div>
                    )}
                  </div>
                  <div className="evaluation-card-actions">
                    <button
                      onClick={() => handleLoadEvaluation(evaluation.id)}
                      className="button primary small"
                    >
                      Load & Edit
                    </button>
                    {onCreateNewForPlayer && (
                      <button
                        onClick={() => onCreateNewForPlayer(
                          evaluation.player_name,
                          evaluation.player_age,
                          evaluation.player_position
                        )}
                        className="button primary small"
                        style={{ marginLeft: '0.5rem', background: '#0165fc', borderColor: '#0165fc' }}
                      >
                        Create New Evaluation
                      </button>
                    )}
                    {onViewPlayerDashboard && evaluation.player_id && (
                      <button
                        onClick={() => onViewPlayerDashboard(evaluation.player_id!)}
                        className="button primary small"
                        style={{ marginLeft: '0.5rem', background: '#4ade80', borderColor: '#4ade80' }}
                      >
                        View Player Dashboard
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          players.length === 0 ? (
            <div className="no-evaluations">
              <p>No players found. Start evaluating players to see them here.</p>
              {onNewEvaluation && (
                <button onClick={onNewEvaluation} className="button primary" style={{ marginTop: '1rem' }}>
                  Create First Evaluation
                </button>
              )}
            </div>
          ) : (
            <div className="evaluations-list">
              <h2>My Players</h2>
              {players.map((player) => (
                <div key={player.player_id || `${player.first_name}-${player.last_name}`} className="evaluation-card">
                  <div className="evaluation-card-header">
                    <h3>{player.first_name} {player.last_name}</h3>
                    {player.latest_evaluation_date && (
                      <span className="evaluation-date">
                        Last Evaluation: {new Date(player.latest_evaluation_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <div className="evaluation-card-details">
                    <div className="detail-item">
                      <span className="detail-label">Age:</span>
                      <span className="detail-value">{player.age}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Position:</span>
                      <span className="detail-value">{player.position}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Evaluations:</span>
                      <span className="detail-value">{player.evaluation_count}</span>
                    </div>
                    {player.latest_weighted_index !== null && player.latest_weighted_index !== undefined && (
                      <div className="detail-item">
                        <span className="detail-label">Latest Score:</span>
                        <span className="detail-value">{player.latest_weighted_index.toFixed(2)}/10</span>
                      </div>
                    )}
                    {player.email && (
                      <div className="detail-item">
                        <span className="detail-label">Email:</span>
                        <span className="detail-value">{player.email}</span>
                      </div>
                    )}
                    {player.whatsapp_phone && (
                      <div className="detail-item">
                        <span className="detail-label">WhatsApp:</span>
                        <span className="detail-value">{player.whatsapp_phone}</span>
                      </div>
                    )}
                  </div>
                  <div className="evaluation-card-actions">
                    {onViewPlayerDashboard && player.player_id && (
                      <button
                        onClick={() => onViewPlayerDashboard(player.player_id!)}
                        className="button primary small"
                        style={{ background: '#4ade80', borderColor: '#4ade80' }}
                      >
                        View Player Dashboard
                      </button>
                    )}
                    {onViewVideoKinematicsForPlayer && player.player_id && (
                      <button
                        onClick={() => onViewVideoKinematicsForPlayer(player.player_id!)}
                        className="button primary small"
                        style={{ background: '#fbbf24', borderColor: '#fbbf24', color: '#000' }}
                      >
                        Video Kinematics
                      </button>
                    )}
                    {onNewEvaluation && (
                      <button
                        onClick={() => onCreateNewForPlayer && onCreateNewForPlayer(
                          `${player.first_name} ${player.last_name}`,
                          player.age,
                          player.position
                        )}
                        className="button primary small"
                        style={{ background: '#0165fc', borderColor: '#0165fc' }}
                      >
                        New Evaluation
                      </button>
                    )}
                    {player.latest_evaluation_id && onLoadEvaluation && (
                      <button
                        onClick={() => handleLoadEvaluation(player.latest_evaluation_id!)}
                        className="button primary small"
                      >
                        View Latest Evaluation
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
      
      {onExit && (
        <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #383b48', textAlign: 'center' }}>
          <button onClick={onExit} className="button ghost" style={{ padding: '0.75rem 2rem' }}>
            Exit
          </button>
        </div>
      )}
    </div>
  );
};

export default CoachDashboard;

